import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useRef, useState, forwardRef } from "react";
import { pointer } from "d3";
import styles from "./styles.module.scss";

interface TooltipProps {
  style: React.CSSProperties;
}

const marginX = 20;
const marginY = 10;

export function useTooltip<Data>(container?: HTMLDivElement | null) {
  const [position, setPosition] = useState({
    x: 0,
    y: 0,
  });
  const [data, setData] = useState<Data | null>(null);
  const [visible, setVisible] = useState(false);
  const shouldHide = useRef(false);
  const tooltipRef = useRef<HTMLDivElement | null>(null);

  // Keep a flag in state to trigger a re-render after the ref has been set.
  // Otherwise the ref will be null on the first render, and the tooltip won't
  // know its width and height (rendering outside of the viewport on mobile).
  const [, setHasRef] = useState(false);

  const is600px = useMediaQuery("(min-width: 600px)");

  const showTooltip = (event: React.MouseEvent, data: Data) => {
    shouldHide.current = false;
    const [x, y] = pointer(event, container);
    setData(data);
    setPosition({ x, y });
    setVisible(true);
  };

  const moveTooltip = (event: React.MouseEvent) => {
    shouldHide.current = false;
    const [x, y] = pointer(event, container);
    setPosition({ x, y });
    setVisible(true);
  };

  const hideTooltip = () => {
    // Don't hide tooltip immediately if we're just moving from one tooltip to another to prevent flashing
    shouldHide.current = true;
    setTimeout(() => {
      if (shouldHide.current) {
        setVisible(false);
        setData(null);
        setHasRef(false);
        shouldHide.current = false;
      }
    }, 50);
  };

  // Make tooltip appear on the left or top if it would otherwise go offscreen
  const containerRect = container?.getBoundingClientRect();
  const containerX = containerRect?.x || 0;
  const containerY = containerRect?.y || 0;
  const containerWidth = containerRect?.width || 0;
  const tooltipWidth = tooltipRef.current?.getBoundingClientRect().width || 275;
  const tooltipHeight =
    tooltipRef.current?.getBoundingClientRect().height || 150;

  let transformX =
    position.x + containerX + tooltipWidth + marginX >= window.innerWidth
      ? "calc(-100% - 5px)"
      : "15px";
  let left = position.x;

  if (!is600px) {
    const isOutOfViewRight =
      position.x + containerX + tooltipWidth / 2 + marginX >= window.innerWidth;
    const isOutOfViewLeft = position.x - tooltipWidth / 2 <= 0;

    transformX = isOutOfViewRight ? "0" : "-50%";
    left = isOutOfViewLeft
      ? tooltipWidth / 2
      : isOutOfViewRight
      ? containerWidth - tooltipWidth
      : left;
  }

  const transformY =
    position.y + containerY + tooltipHeight + marginY >= window.innerHeight
      ? "calc(-100% - 5px)"
      : "5px";

  return {
    showTooltip,
    moveTooltip,
    hideTooltip,
    tooltipData: data,
    tooltipVisible: visible,
    tooltipProps: {
      style: {
        ...styles,
        left,
        top: position.y,
        transform: `translate(${transformX}, ${transformY})`,
      },
      ref: (el: HTMLDivElement) => {
        tooltipRef.current = el;
        if (el) {
          setHasRef(true);
        } else if (!el && tooltipRef.current) {
          setHasRef(false);
        }
      },
    },
  };
}

function TooltipInner(
  { style, children }: React.PropsWithChildren<TooltipProps>,
  forwardedRef: React.Ref<HTMLDivElement>
) {
  return (
    <div className={styles.container} style={style} ref={forwardedRef}>
      {children}
    </div>
  );
}

export const Tooltip = forwardRef(TooltipInner);
